import type { PanelComponentSpec } from '@src/types/LayoutConfig';
import { CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_SUPPORT } from '../../../../containers/Blotters/CustomerAssetTransactions/useCustomerAssetTransactionsFilter';
import { CUSTOMER_ORDER_SPACE_PARAMETER_SUPPORT } from '../../../../containers/Blotters/CustomerOrders/useCustomerOrdersFilter';
import { CUSTOMER_TRADE_SPACE_PARAMETER_SUPPORT } from '../../../../containers/Blotters/CustomerTrades/useCustomerTradeFilter';
import { CUSTOMER_BALANCES_SPACE_PARAMETER_SUPPORT } from '../../../../containers/Dealer/CustomerBalances/useCustomerBalancesFilter';
import { CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_SUPPORT } from '../../../../containers/Dealer/CustomerTransactions/useCustomerTransactionsFilter';
import { CustomerAccountConfigurationsPanel } from '../Dealer/CustomerAccountConfigurationsPanel';
import { CustomerAccountsPanel } from '../Dealer/CustomerAccountsPanel';
import { CustomerAddressesPanel } from '../Dealer/CustomerAddressesPanel';
import { CustomerAssetTransactionsPanel } from '../Dealer/CustomerAssetTransactionsPanel';
import { CustomerCreditsPanel } from '../Dealer/CustomerCreditsPanel';
import { CustomerFIXConnectionsPanel } from '../Dealer/CustomerFIXConnectionsPanel';
import { CustomerFundingRateConfigurationPanel } from '../Dealer/CustomerFundingRateConfigurationPanel';
import { CustomerMarginConfigurationPanel } from '../Dealer/CustomerMarginConfigurationPanel';
import { CustomerPositionsPanel } from '../Dealer/CustomerPositionsPanel';
import { CustomerScheduleConfigurationPanel } from '../Dealer/CustomerScheduleConfigurationPanel';
import { CustomerUsersPanel } from '../Dealer/CustomerUsersPanel';
import {
  CUSTOMER_ACCOUNT_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_ACCOUNTS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_ADDRESSES_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_CREDITS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_FUNDING_RATE_CONFIGURATION_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_POSITIONS_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_USERS_SPACE_PARAMETER_SUPPORT,
} from '../Dealer/DealerSpaceParameters';
import { DealerSpaceParametersPanel } from '../Dealer/DealerSpaceParametersPanel';
import { CustomerBalancesPanel } from './blotters/CustomerBalancesPanel';
import { CustomerOrdersPanel } from './blotters/CustomerOrdersPanel';
import { CustomerTradesPanel } from './blotters/CustomerTradesPanel';
import { CustomerTransfersPanel } from './blotters/CustomerTransfersPanel';
import { PANEL_COMPONENT_GROUPS } from './groups';

/** Dealer > Monitoring blotters. Not separate sub-nav leaves. */
export const CUSTOMER_MONITORING_PANEL_COMPONENTS: PanelComponentSpec[] = [
  {
    // Deprecated unused panel. Space parameters are edited from the page header.
    group: undefined,
    section: 'dealer',
    type: 'dealerSpaceParameters',
    label: 'Space Parameters',
    icon: undefined,
    canBeAdded: false,
    minWidth: 280,
    minHeight: 320,
    renderer: DealerSpaceParametersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['monitoring'],
    section: 'dealer',
    type: 'customerOrdersBlotter',
    spaceParameterSupport: CUSTOMER_ORDER_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Orders',
    icon: undefined,
    canBeAdded: true,
    renderer: CustomerOrdersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['monitoring'],
    section: 'dealer',
    type: 'customerTradesBlotter',
    spaceParameterSupport: CUSTOMER_TRADE_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Trades',
    icon: undefined,
    canBeAdded: true,
    renderer: CustomerTradesPanel,
  },
];

/** Dealer > Buying Power leaves. Labels match `navigationPaths.ts`. */
export const CUSTOMER_BUYING_POWER_PANEL_COMPONENTS: PanelComponentSpec[] = [
  {
    group: PANEL_COMPONENT_GROUPS['buying-power'],
    section: 'dealer',
    type: 'customerBalancesBlotter',
    spaceParameterSupport: CUSTOMER_BALANCES_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Balances',
    icon: undefined,
    canBeAdded: true,
    renderer: CustomerBalancesPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['buying-power'],
    section: 'dealer',
    type: 'customerTransfersBlotter',
    spaceParameterSupport: CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Transfers',
    icon: undefined,
    canBeAdded: true,
    renderer: CustomerTransfersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['buying-power'],
    section: 'dealer',
    type: 'customerCredits',
    spaceParameterSupport: CUSTOMER_CREDITS_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Credit',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerCreditsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['buying-power'],
    section: 'dealer',
    type: 'customerAddresses',
    spaceParameterSupport: CUSTOMER_ADDRESSES_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Addresses',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAddressesPanel,
  },
];

/** Dealer > Admin leaves. Customer Margin contributes several EntityAdmin panels. */
export const CUSTOMER_ADMIN_PANEL_COMPONENTS: PanelComponentSpec[] = [
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerUsers',
    spaceParameterSupport: CUSTOMER_USERS_SPACE_PARAMETER_SUPPORT,
    label: 'Users',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerUsersPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerAccounts',
    spaceParameterSupport: CUSTOMER_ACCOUNTS_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Accounts',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAccountsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerAccountConfigurations',
    spaceParameterSupport: CUSTOMER_ACCOUNT_CONFIGURATIONS_SPACE_PARAMETER_SUPPORT,
    label: 'Account Configurations',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAccountConfigurationsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerPositions',
    spaceParameterSupport: CUSTOMER_POSITIONS_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Positions',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerPositionsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerMarginConfiguration',
    label: 'Margin Configuration',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerMarginConfigurationPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerFundingRateConfiguration',
    spaceParameterSupport: CUSTOMER_FUNDING_RATE_CONFIGURATION_SPACE_PARAMETER_SUPPORT,
    label: 'Funding Rate Configuration',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerFundingRateConfigurationPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerScheduleConfiguration',
    label: 'Schedule Configuration',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerScheduleConfigurationPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerAssetTransactions',
    spaceParameterSupport: CUSTOMER_ASSET_TRANSACTIONS_SPACE_PARAMETER_SUPPORT,
    label: 'Customer Asset Transactions',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerAssetTransactionsPanel,
  },
  {
    group: PANEL_COMPONENT_GROUPS['admin'],
    section: 'dealer',
    type: 'customerFIXConnections',
    label: 'FIX Connections',
    icon: undefined,
    canBeAdded: true,
    minHeight: 200,
    renderer: CustomerFIXConnectionsPanel,
  },
];
